import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { gql } from 'graphql-request'
import { useMutation } from '@hooks/graphql'
import { useAnalytics } from '@contexts/analytics'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import BasicLayout from '@/components/Onboarding/Layout/BasicLayout'
import { useFlashMessage } from '@/components/FlashMessage'

const CREATE_ORGANIZATION_MUTATION = gql`
  mutation createOrganization($input: CreateOrganizationInput!) {
    createOrganization(input: $input) {
      organization {
        id
        name
      }
    }
  }
`

const CreateOrganization = ({ nextStep }) => {
  const formSchema = z.object({
    name: z.string().nonempty({ message: 'Workspace Name is required' }),
    organizationType: z.string().nonempty({ message: 'Organization Type is required'})
  })

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      organizationType: ''
    }
  })

  const { track } = useAnalytics()
  const { setFlashMessage } = useFlashMessage()
  const { mutate: createOrganization, isLoading: isCreating } = useMutation({
    gqlMutation: CREATE_ORGANIZATION_MUTATION,
    onSuccess: () => {
      track('Educator Onboarding Step Completed', { step: 'organization' })
      nextStep()
    },
    onError: (error) => {
      setFlashMessage('Something went wrong', 'error')
    }
  })

  const onSubmit = (data) => {
    createOrganization({ input: data })
  }

  return (
    <BasicLayout>
      <h1 className='text-2xl font-extrabold tracking-tight'>
        Create a workspace
      </h1>

      <p className='my-2'>
        Don't worry, you'll be able to tweak details later! If you are trying to join an existing workspace, ask an existing member to send you an invite.
      </p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6 mt-6'>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Workspace Name</FormLabel>
                <FormControl>
                  <Input placeholder='e.g. Greenfield Academy' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="organizationType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization Type</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl className='w-full'>
                    <SelectTrigger>
                      <SelectValue placeholder='Select answer' />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value='school_k12'>School (K12)</SelectItem>
                    <SelectItem value='university'>University</SelectItem>
                    <SelectItem value='online_school'>Online school</SelectItem>
                    <SelectItem value='microschool'>Microschool</SelectItem>
                    <SelectItem value='homeschool'>Homeschool</SelectItem>
                    <SelectItem value='tutor_programme'>Tutor programme</SelectItem>
                    <SelectItem value='ngo'>NGO</SelectItem>
                    <SelectItem value='other'>Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type='submit' loading={isCreating}>Continue</Button>
        </form>
      </Form>
    </BasicLayout>
  )
}

export default CreateOrganization
